import React from "react";
import { connect } from "react-redux";
import {
  isLandscape,
  isMobileDevice,
  isServer,
  loadScript,
} from "../../../utils";
import { User } from "../../models/User";
import { Canvas } from "./Canvas";
import { AppInsightsAnalytics } from "../../services/AppInsight";
import styles from "./Game.css";
import { GameLoading } from "../../atoms/Loading/Loading";
import { getAspectRatioByGame } from "../../constants/GameAspectRatioData";
import AARPAuthService from "../../services/AARPAuthService";
import { GameState } from "../../models/Enums";
import classNames from "classnames";
import { setTheaterMode } from "../../store/reducers";
import Modal from "../../atoms/Modal/Modal";
import ThemeSettings from "../../../theme";
import { Analytics } from "../../services/Analytics/Analytics";
import GameMobileAd from "../../molecules/GameMobileAd/GameMobileAd";
import { isMobile } from "../../services/DeviceDetector";
import { Subject } from "../../models/GameObservable";
import { ResizeBox, handlerADContent } from "../../molecules/ResizeBox/ResizeBox";

export type GameUnitProps = {
  user: User;
  game: any;
  gameState: GameState;
  dispatch?: any;
  isTheaterMode: boolean;
  isDisplayAdsEnabled: boolean;
  onScoreChange: any;
  onGameEnd: any;
  onReward: any;
  onPowerUp: any;
  onOptInLogin: any;
};

type CustomProperties = {
  name?: string;
  loginUrl: string;
  GDPRUserData?: boolean;
  aarpMembershipStatus?: string;
  aarpMembershipExpirationDate?: string;
  aarpUid?: string;
  countryCode?: string;
  email?: string;
  firstName?: string;
  id?: string;
  lastName?: string;
  userAuthSystem?: string;
  GDPREmailMarketing?: boolean;
  isOptInToReward?: boolean;
};

interface RootState {
  isTheaterMode: boolean;
}

class GameUnitDumb extends React.Component<GameUnitProps> {
  gameSubject: Subject = new Subject(this);

  containerRef = React.createRef<HTMLDivElement>();
  canvasRef = React.createRef<HTMLDivElement>();

  state = {
    isGameLoading: true,
    showLoginModal: false,
    isLandscape: null,
    isPhone: isMobile(),
    isTablet: isMobileDevice() && !isMobile(),
  };

  onResizeClick = () => {
    const { game, isTheaterMode, user } = this.props;
    Analytics.trackEvent(
      Analytics.games.theaterModeClick(game, !isTheaterMode)
    );

    const element = document.getElementById("game-container");
    if (user) {
      if (element && !isTheaterMode) {
        window.scrollTo({ top: element.scrollHeight });
      }
      this.setIsTheaterMode();
      this.handlerCloseLoginModal();
    } else {
      this.setState({ showLoginModal: true });
    }
  };

  handlerCloseLoginModal() {
    this.setState({ showLoginModal: false });
  }

  handlerLogin() {
    location.href = AARPAuthService.getNewLoginUrl();
  }

  handlerRegister() {
    location.href = AARPAuthService.getRegisterUrl();
  }

  loginModal() {
    return (
      <Modal
        modalTheme="secondary"
        header="AARP REGISTERED USER FEATURE"
        headerTitle="Log in to play in full-width Theater Mode"
        footerText="Not Registered? Create Account >"
        content="Best used before game start to save your game progress"
        onClose={() => this.handlerCloseLoginModal()}
        onConfirm={() => this.handlerLogin()}
        footerClick={() => this.handlerRegister()}
        confirmButtonText="Log In"
        isSectionModal
      />
    );
  }

  setIsTheaterMode = async () => {
    await this.props.dispatch(setTheaterMode(!this.props.isTheaterMode));
    handlerADContent({ isTheaterMode: this.props.isTheaterMode });
    this.onResize();
  };

  onResize = () => {
    const element = this.containerRef.current as any;
    const canvas = this.canvasRef.current as any;
    let componentHeight: number;

    if (!element || !canvas || isServer) {
      return;
    }

    if (this.state.isPhone) {
      this.setState({ isLandscape: isLandscape() });
    }

    if (isMobileDevice()) {
      window.scrollTo(0, 0);
      document.body.style.overflow = "hidden";
      componentHeight = window.innerHeight;
      const iframe = canvas.querySelector("iframe");
      if (iframe) {
          const MOBILE_AD_SIZE_BAR = this.state.isLandscape ? 50 : 100;
          iframe.width = window.innerWidth;
          iframe.height = componentHeight - MOBILE_AD_SIZE_BAR;
      }
    } else {
      document.body.style.overflow = "auto";
      const parent = element.parentNode.parentNode;
      const width = parent.offsetWidth;
      const AARP_STICKY_HEADER_HEIGHT = 60;
      const GRAY_BAR_AREA_CONTENT = 100;
      const gameAspectRatio = getAspectRatioByGame(this.props.game.slug);
      componentHeight = Math.min(
        Math.ceil(width * gameAspectRatio),
        window.innerHeight - AARP_STICKY_HEADER_HEIGHT - GRAY_BAR_AREA_CONTENT
      );

      if (
        canvas.firstChild.firstChild &&
        canvas.firstChild.firstChild.tagName === "IFRAME"
      ) {
        canvas.firstChild.firstChild.width = width;
        canvas.firstChild.firstChild.height = componentHeight;
      }
    }

    if (isMobileDevice()) {
      canvas.style.height = `auto`;
    }else {
      canvas.style.height = `${componentHeight}px`;
    }
  };

  componentWillUnmount() {
    this.props.dispatch(setTheaterMode(false));
    if (!isServer) {
      window.removeEventListener("resize", this.onResize);
      window.removeEventListener("orientationchange", this.onResize);
    }
  }

  getOptInToRewardsStatus() {
    return (
      window["AARP"].store.getState().cookie.at &&
      window["AARP"].store.getState().cookie.at.rs &&
      window["AARP"].store.getState().cookie.at.rs == "Y"
    );
  }

  //Load Game without GRM
  loadGame() {
    const { assetOriginUrl, sdkName } = this.props.game;
    loadScript(`${assetOriginUrl}main.min.js`).then(() => {
      let attempts = 0;
      const maxPollingAttempts = 50;
      const pollingInterval = 100;

      const waitForGame = () => {
        attempts++;
        const arenax = (window as any).__arenax_v1__;
        const gameContainer = arenax && arenax[`${sdkName}Game`];
        const sdk = gameContainer && gameContainer.game;

        if (sdk) {
          try {
             this.initializeGame(sdk, assetOriginUrl);
          } catch (e) {
            console.error('testt', e);
            AppInsightsAnalytics.trackAppError("game register/render failed", {
              message: e.message,
              stack: e.stack || "",
            });
          }
        } else if (attempts < maxPollingAttempts) {
          setTimeout(waitForGame, pollingInterval);
        } else {
          console.error("testt Game object not found after maximum attempts");
          AppInsightsAnalytics.trackAppError("game object not found", {
            message: "Game object not found after maximum attempts",
          });
        }
      };

      waitForGame();
    });
  }

  initializeGame(sdk: any, assetOriginUrl: string) {
    const customProperties: CustomProperties = {
      loginUrl: AARPAuthService.getNewLoginUrl(),
    };
  
    if (this.props.user) {
      customProperties.name = this.props.user.name;
      customProperties.aarpMembershipStatus = this.props.user.aarpMembershipStatus;
      customProperties.id = this.props.user.id;
      customProperties.isOptInToReward = this.getOptInToRewardsStatus();
      customProperties.email = this.props.user.email;
    }
  
    let supportRewardVideo =
      this.props.game.seo_IsSupportRewardVideo === undefined
        ? false
        : this.props.game.seo_IsSupportRewardVideo;
  
    sdk.register(this.gameSubject, {
      assetOriginUrl,
      configUrl: "",
      locale: "en",
      eventList: [],
      rewardVideoAvailable: supportRewardVideo,
      customProperties,
    });
  
    sdk.render(0, 0, "game-canvas");
  }

  componentDidMount() {
    if (isServer) {
      return;
    }
    (window as any).arenaName = "AARP";

    this.onResize();
    window.addEventListener("orientationchange", this.onResize);
    window.addEventListener("resize", this.onResize);
    this.loadGame();
  }

  componentDidUpdate(prevProps: Readonly<GameUnitProps>): void {
    if (prevProps.gameState !== this.props.gameState) {
        if(isMobileDevice()) {
            this.onResize();
        }
    }
}

  render() {
    const { game, gameState, isTheaterMode, user, isDisplayAdsEnabled } =
      this.props;
    const isGameStateGame = gameState === GameState.GAME;
    const isDisplayAdsVisible =
      isDisplayAdsEnabled && this.state.isPhone && isGameStateGame;

    return (
      <UnitContainer
        data-element-description="game"
        ref={this.containerRef}
        hidden={
          gameState === GameState.PREROLL || gameState === GameState.REWARD
        }
        isPhone={this.state.isPhone}
        isTablet={this.state.isTablet}
      >
        {isDisplayAdsVisible && (
          <GameAd data-element-description="game top ad">
              <GameMobileAd
                componentId="ark_display_m1"
                dataElementDescription="ark-display-m1"
                dimensions={this.state.isLandscape ? [[320, 50]] : [[320, 100]]}
              />
          </GameAd>
        )}

        <CanvasContainer
          ref={this.canvasRef}
          isPhone={this.state.isPhone}
          isTablet={this.state.isTablet}
          hidden={
            gameState === GameState.PREROLL || gameState === GameState.REWARD
          }
        >
          <Canvas
            id="game-canvas"
            hidden={
              gameState === GameState.PREROLL || gameState === GameState.REWARD
            }
            shouldComponentUpdate
          />
        </CanvasContainer>

        {this.state.isGameLoading ? <GameLoading /> : null}

        {!isMobileDevice() &&
          window.innerWidth > ThemeSettings.breakPoints.desktop &&
          game.seo_TheaterMode && (
            <ResizeBox isTheaterMode={isTheaterMode} user={user} onResizeClick={this.onResizeClick} />
          )}
        {this.state.showLoginModal && this.loginModal()}
      </UnitContainer>
    );
  }
}

const UnitContainer = React.forwardRef<any, any>(
  ({ hidden, isTablet, isPhone, ...props }: any, ref) => (
    <div
      className={classNames(styles.gameUnitContainer, {
        [styles.hidden]: hidden,
        [styles.fullScreen]: isPhone || isTablet,
      })}
      {...props}
      ref={ref}
    />
  )
);
const CanvasContainer = React.forwardRef<any, any>(
  ({ isPhone, isTablet, hidden, ...restProps }: any, ref) => {
    return (
      <div
        className={classNames(styles.unitCanvasContainer, {
          [styles.fullScreenMob]: isPhone,
          [styles.fullScreenTab]: isTablet,
          [styles.hidden]: hidden
        })}
        {...restProps}
        ref={ref}
      />
    );
  }
);

const GameAd = (props: any) => <div className={styles.gameAd} {...props} />;

export const GameUnit = connect((state: RootState) => ({
  isTheaterMode: state.isTheaterMode,
}))(GameUnitDumb);
